import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import Host from "../../Components/Host";
import ReCAPTCHA from "react-google-recaptcha";
import { useFindOrderMutation, useVerifyMutation } from "../../services/track_order";
import OtpInput from "react-otp-input";
import Loader from "../../Components/Loader";
import HelmetComponent from "../../Components/HelmetComponent";
import { Tag } from "antd";
import { Link } from "react-router-dom";
import { Box } from "@mui/material";
import "./index.scss";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { storeIds } from "../../constants/storeIds";

var moment = require("moment");

const SUMMARY_STAGES = [
  { key: "processing", label: "Processing", icon: Inventory2RoundedIcon },
  { key: "dispatched", label: "Procuring", icon: InventoryRoundedIcon },
  { key: "shipping", label: "Shipping", icon: LocalShippingRoundedIcon },
  { key: "delivered", label: "Delivered", icon: HomeRoundedIcon },
];

function buildDetailedStages(country) {
  const stages = [
    { key: "processing", label: "Processing", sub: "Estimated 1 day" },
    {
      key: "dispatched",
      label: "Procuring",
      sub: country === "AE" ? "Estimated 1-2 days" : "Estimated 2-3 days",
    },
    { key: `dispatched_from_supplier`, label: "Dispatched from supplier" },
    {
      key: "shipping",
      label: "Shipping and fulfilment",
      sub: country === "AE" ? "Estimated 1-2 days" : "Estimated 3-5 days",
    },
  ];

  if (country && country !== "AE") {
    stages.push(
      { key: `arrived_in_${country}`, label: `Arrived in ${country} warehouse` },
      { key: `departed_from_${country}`, label: `Departed from ${country} warehouse` }
    );
  }

  stages.push(
    { key: "arrived_in_uae", label: "Arrived in UAE warehouse" },
    { key: "departed_from_uae", label: "Departed from UAE warehouse" },
    { key: "out_for_delivery", label: "Out for delivery" },
    {
      key: "delivered",
      label: "Delivered",
      sub: country === "AE" ? "Estimated 1-2 days" : "Estimated 2-3 days",
    }
  );

  return stages;
}

function buildRawPipeline(country) {
  const pipeline = ["dispatched"];
  if (country && country !== "AE") {
    pipeline.push(`arrived_in_${country}`, `departed_from_${country}`);
  }
  pipeline.push("arrived_in_uae", "departed_from_uae", "out_for_delivery", "delivered");
  return pipeline;
}

function getFurthestStageIndex(shippingStatus, country) {
  const history = (shippingStatus || "").split(",").map((s) => s.trim());
  const pipeline = buildRawPipeline(country);
  let furthest = -1;
  pipeline.forEach((stage, idx) => {
    if (history.includes(stage) && idx > furthest) furthest = idx;
  });
  return furthest;
}

function getStageStatus(stageKey, furthestIndex, country, shippingState) {
  if (stageKey === "processing") {
    if (furthestIndex >= 0 || shippingState !== "processing") return furthestIndex >= 0 ? "done" : "done";
    return "active";
  }

  const pipeline = buildRawPipeline(country);

  if (stageKey === "dispatched" || stageKey === "dispatched_from_supplier") {
    if (furthestIndex >= 0) return "done";
    return shippingState === "dispatched" ? "active" : "upcoming";
  }

  if (stageKey === "shipping") {
    const shippingIdx = pipeline.indexOf("arrived_in_uae");
    if (furthestIndex >= shippingIdx) return "done";
    return furthestIndex >= 0 ? "active" : "upcoming";
  }

  const idx = pipeline.indexOf(stageKey);
  if (idx === -1) return "upcoming";
  if (furthestIndex > idx) return "done";
  if (furthestIndex === idx) return shippingState === "delivered" && stageKey === "delivered" ? "done" : "done";
  if (shippingState === stageKey) return "active";
  return "upcoming";
}

function getSummaryStageStatus(stageKey, furthestIndex, country, shippingState) {
  const pipeline = buildRawPipeline(country);
  const shippingStartIdx = pipeline.indexOf("dispatched") + 1; // first index counted as "shipping"

  if (stageKey === "processing") {
    return furthestIndex >= -1 && shippingState === "processing" ? "active" : "done";
  }
  if (stageKey === "dispatched") {
    if (furthestIndex >= 0) return "done";
    return shippingState === "dispatched" ? "active" : "upcoming";
  }
  if (stageKey === "shipping") {
    const arrivedUaeIdx = pipeline.indexOf("arrived_in_uae");
    if (furthestIndex >= arrivedUaeIdx) return "done";
    if (furthestIndex >= shippingStartIdx - 1 && furthestIndex >= 0) return "active";
    return "upcoming";
  }
  if (stageKey === "delivered") {
    return shippingState === "delivered" ? "done" : "upcoming";
  }
  return "upcoming";
}

function SummaryTimeline({ item }) {
  const furthestIndex = getFurthestStageIndex(item.shipping_status, item.country);

  return (
    <div className="dt-summary">
      {SUMMARY_STAGES.map((stage, idx) => {
        const status = getSummaryStageStatus(stage.key, furthestIndex, item.country, item.shipping_state);
        const Icon = stage.icon;
        const isLast = idx === SUMMARY_STAGES.length - 1;
        return (
          <div className={`dt-summary-step dt-summary-step--${status}`} key={stage.key}>
            <div className="dt-summary-node">
              <div className="dt-summary-dot">
                {status === "done" ? <CheckRoundedIcon style={{ fontSize: 16 }} /> : <Icon style={{ fontSize: 16 }} />}
              </div>
              {!isLast && <div className="dt-summary-line" />}
            </div>
            <span className="dt-summary-label">{stage.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function DetailedTimeline({ item }) {
  const stages = buildDetailedStages(item.country);
  const furthestIndex = getFurthestStageIndex(item.shipping_status, item.country);

  return (
    <div className="dt-detail">
      {stages.map((stage, idx) => {
        const status = getStageStatus(stage.key, furthestIndex, item.country, item.shipping_state);
        const isLast = idx === stages.length - 1;
        return (
          <div className={`dt-detail-row dt-detail-row--${status}`} key={stage.key + idx}>
            <div className="dt-detail-rail">
              <div className="dt-detail-node">
                {status === "done" ? <CheckRoundedIcon style={{ fontSize: 13 }} /> : null}
              </div>
              {!isLast && <div className="dt-detail-connector" />}
            </div>
            <div className="dt-detail-content">
              <span className="dt-detail-label">{stage.label}</span>
              {stage.sub && <span className="dt-detail-sub">{stage.sub}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TrackOrder() {
  const { countryCode, languageCode } = useSelector((state) => state.auth);
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  const fontFamilyForAll = languageCode === "ar" ? "GessTwoMedium, sans-serif" : "Poppins, sans-serif";
  const fontFamily = { fontFamily: fontFamilyForAll };

  const [findorder, findresult] = useFindOrderMutation();
  const [verifyotp, verifyresult] = useVerifyMutation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const { handleSubmit: handleSubmit2 } = useForm();
  const recaptchaRef = React.useRef();
  const [isOtpCard, setOtpCard] = useState(false);
  const [isLoginCard, setLoginCard] = useState(true);
  const [otp, setOtp] = useState("");
  const [getToken, setToken] = useState("");
  const [isExpand, setExpand] = useState("");

  async function loginWithOtp() {
    const token = await recaptchaRef.current.executeAsync();
    recaptchaRef.current.reset();
    verifyotp({
      otp: otp,
      token: getToken,
      captcha_token: token,
      countryCode: countryCode,
      domain: Host?.domain,
      domainEmail: Host?.email,
    });
  }

  async function trackOrder(data) {
    const token = await recaptchaRef.current.executeAsync();
    recaptchaRef.current.reset();
    findorder({
      order_number: data.order_number,
      email: data.email,
      captcha_token: token,
      countryCode: countryCode,
      domain: window.location.hostname || Host?.domain,
      domainEmail: Host?.email,
    });
  }

  useEffect(() => {
    if (findresult?.isSuccess) {
      setToken(findresult?.data?.token);
      setLoginCard(false);
      setOtpCard(true);
    }
    if (verifyresult?.isSuccess) {
      setLoginCard(false);
      setOtpCard(false);
    }
    if (!verifyresult?.isSuccess && !findresult?.isSuccess) {
      setLoginCard(true);
    }
  }, [findresult, verifyresult]);

  function getDeliverydays(data, created) {
    if (Number(data) > 0) {
      return moment(created).add(Number(data), "days").format("dddd Do MMM YYYY");
    }
    return moment(created).add(10, "days").format("dddd Do MMM YYYY");
  }

  const order = verifyresult?.data?.order;

  const groupData = order?.order_items?.reduce((acc, obj) => {
    const key = obj.estimated_delivery;
    if (!acc[key]) acc[key] = [];
    acc[key].push(obj);
    return acc;
  }, {});

  function functionIdLink(id, pid, store_id) {
    return storeIds?.includes(Number(store_id)) ? pid : id;
  }

  function productTitle(title) {
    return title
      ?.replace(/[^A-Za-z0-9]/g, " ")
      ?.replace(/[^\w\s.,]/gi, " ")
      ?.replace(/ /g, "-");
  }

  function renderOrderItems() {
    return (
      <div className="dt-groups">
        {groupData &&
          Object.entries(groupData).map(([group, groupItems]) => (
            <div className="dt-group" key={group}>
              <div className="dt-group-header">
                <div className="dt-group-header-left">
                  <LocalShippingOutlinedIcon sx={{ fontSize: 18, color: "#ff7043" }} />
                  <span className="dt-group-eta">
                    Estimated delivery&nbsp;
                    <strong>{getDeliverydays(group, order?.createdAt)}</strong>
                  </span>
                </div>
                <span className="dt-group-count">
                  {groupItems?.length} {groupItems?.length === 1 ? "item" : "items"}
                </span>
              </div>

              {groupItems?.map((item, index) => {
                const isCancelled = item.state === "cancelled";
                const isExpanded = isExpand === `open${item.id}`;
                return (
                  <div className="dt-item-card" key={index}>
                    {!isCancelled && (
                      <div className="dt-item-card-timeline">
                        <SummaryTimeline item={item} />
                      </div>
                    )}

                    <div className="dt-item-card-body">
                      <div className="dt-item-img-link">
                        <img className="dt-item-img" src={item.image} alt={item.title} />
                      </div>

                      <div className="dt-item-info">
                        <div className="dt-item-title-link">
                          <p className="dt-item-title">{item.title?.replace(/[^\w\s.,/:']/gi, " ")}</p>
                        </div>

                        <div className="dt-item-tags">
                          <Tag>Condition: {item.conditions}</Tag>
                          <Tag>Qty.: {item.quantity}</Tag>
                          {item.current_variant &&
                            Object.entries(item.current_variant || []).map(([key, value], i) => (
                              <Tag key={i} style={{ fontSize: "10px" }}>
                                <strong>{key}:</strong> {value}
                              </Tag>
                            ))}
                        </div>

                        <div className="dt-item-status-row">
                          {isCancelled ? (
                            <span className="dt-status-pill dt-status-pill--cancelled">Item cancelled</span>
                          ) : item.shipping_state === "delivered" ? (
                            <span className="dt-status-pill dt-status-pill--delivered">
                              {t("DELIVERED ON")} {moment(item.delivered_on).format("Do MMM YYYY, HH:mm")}
                            </span>
                          ) : (
                            <span className="dt-status-pill dt-status-pill--active">
                              {item.shipping_state?.replace(/_/g, " ")}
                            </span>
                          )}
                        </div>
                      </div>

                      {!isCancelled && (
                        <button
                          type="button"
                          className="dt-tracking-toggle"
                          onClick={() => setExpand(isExpanded ? "" : `open${item.id}`)}
                          aria-expanded={isExpanded}
                        >
                          <span>Tracking details</span>
                          <KeyboardArrowDownRoundedIcon
                            style={{
                              fontSize: 20,
                              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </button>
                      )}
                    </div>

                    {!isCancelled && isExpanded && (
                      <div className="dt-item-card-expanded">
                        <DetailedTimeline item={item} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
      </div>
    );
  }

  return (
    <>
      <HelmetComponent title={"Track order"} />
      <div className="dt-page">
        <div className="dt-container">
          {!order && (
            <div className="dt-login-card">
              {(findresult?.error?.data?.error || verifyresult?.error?.data?.error) && (
                <div className="dt-error-banner">
                  {findresult?.error?.data?.error || verifyresult?.error?.data?.error}
                </div>
              )}

              <div className="dt-login-icon">
                <Box
                  sx={{
                    width: 86,
                    height: 86,
                    borderRadius: "50%",
                    background: "#fff7f2",
                    border: "1px solid #ffe1d2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 10px 28px rgba(255, 113, 68, 0.14)",
                  }}
                >
                  <LocalShippingOutlinedIcon sx={{ fontSize: 38, color: "#ff7043" }} />
                </Box>
              </div>

              <h3 className="dt-login-title">{t("Track your order")}</h3>
              <p className="dt-login-subtitle">
                Enter your order number and the email used at checkout. We'll send an OTP to verify and show your order
                status.
              </p>

              {isLoginCard && (
                <form onSubmit={handleSubmit(trackOrder)}>
                  <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey="6LfD62EnAAAAAJ83k0BznR8m_cXInxBFjtTokspL" />

                  <div className="dt-field">
                    <label htmlFor="order_number">{t("Order Number")}</label>
                    <input
                      className={`dt-input ${errors.order_number ? "dt-input--error" : ""}`}
                      type="text"
                      id="order_number"
                      aria-invalid={errors.order_number ? "true" : "false"}
                      {...register("order_number", { required: true })}
                    />
                  </div>

                  <div className="dt-field dt-field--last">
                    <label htmlFor="email">{t("email")}</label>
                    <input
                      className={`dt-input ${errors.email ? "dt-input--error" : ""}`}
                      type="email"
                      id="email"
                      aria-invalid={errors.email ? "true" : "false"}
                      {...register("email", { required: true })}
                    />
                  </div>

                  <Button
                    disabled={findresult.isLoading}
                    className="dt-submit-btn"
                    style={{ background: Host?.bgColor || "#000", color: Host?.buttonColor || "#fff" }}
                    variant="info"
                    type="submit"
                  >
                    <span style={fontFamily}>{findresult.isLoading ? <Loader /> : t("submit")}</span>
                  </Button>
                </form>
              )}

              {isOtpCard && (
                <form onSubmit={handleSubmit2(loginWithOtp)}>
                  <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey="6LfD62EnAAAAAJ83k0BznR8m_cXInxBFjtTokspL" />

                  <div className="dt-otp-block">
                    <label>{t("Enter your OTP")}</label>
                    <p className="dt-otp-subtitle">We've sent a 6-digit code to your email</p>
                    <OtpInput
                      inputStyle="inputStyle"
                      containerStyle="otp otp-verify"
                      value={otp}
                      onChange={setOtp}
                      numInputs={6}
                      renderSeparator={<div style={{ padding: "5px" }}></div>}
                      id="code"
                      renderInput={(props) => <input {...props} />}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={verifyresult?.isLoading}
                    className="dt-submit-btn"
                    style={{ background: Host?.bgColor || "#000", color: Host?.buttonColor || "#fff" }}
                    variant="info"
                  >
                    <span style={fontFamily}>{verifyresult?.isLoading ? <Loader /> : t("submit")}</span>
                  </Button>
                </form>
              )}
            </div>
          )}

          {order && (
            <div className="dt-order-wrap">
              <div className="dt-order-header">
                <h3 className="dt-order-heading">{t("Order Details")}</h3>
              </div>

              <div className="dt-order-grid">
                <div className="dt-order-main">{renderOrderItems()}</div>

                <div className="dt-order-side">
                  <div className="dt-summary-card">
                    <div className="dt-summary-card-row">
                      <span className="dt-summary-card-label">Order Number</span>
                      <span className="dt-summary-card-value">{order?.order_number}</span>
                    </div>
                    <div className="dt-summary-card-row">
                      <span className="dt-summary-card-label">Total Items</span>
                      <span className="dt-summary-card-value">{order?.order_items?.length}</span>
                    </div>
                    <div className="dt-summary-card-divider" />
                    <div className="dt-summary-card-row">
                      <span className="dt-summary-card-label">Order Status</span>
                      {order?.states === "cancelled" ? (
                        <span className="dt-status-pill dt-status-pill--cancelled">Cancelled</span>
                      ) : order?.status ? (
                        <span className="dt-status-pill dt-status-pill--delivered">Confirmed</span>
                      ) : (
                        <span className="dt-status-pill dt-status-pill--active">Payment pending</span>
                      )}
                    </div>
                    <div className="dt-summary-card-row">
                      <span className="dt-summary-card-label">{t("order_placed_on")}</span>
                      <span className="dt-summary-card-value">{moment(order?.createdAt).format("MMM Do YYYY")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default TrackOrder;
